import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  ERadixListView,
  ERadixProgram,
  IRadixDocumentsActionRequest,
  IRadixDocumentsActionResponse,
  IRadixFilePreview,
  IRadixGenerateForAllRequest,
  IRadixGenerateForAllResponse,
  IRadixImportProgress,
  IRadixImportRequest,
  IRadixImportStartResponse,
  IRadixListResponse,
  IRadixSettings,
  IRadixSourceDocumentsResponse,
  ISaveRadixSettingsRequest,
  ITestRadixConnectionRequest,
  ITestRadixConnectionResponse,
} from "dto/Radix/IRadix";

// Moduł RADIX — import paczek dokumentów z programów RADIX (WIP / PoGrun / GOK),
// listy (Decyzje / Upomnienia / Zawiadomienia / Do wysłania / Wysłane) i obieg do
// biura podawczego. BE: RadixController (api/radix/*).

export const radixQueryKeys = {
  settings: ["radix.settings"],
  list: (view: ERadixListView, search: string) => ["radix.list", view, search],
  source: (program: ERadixProgram) => ["radix.source", program],
  progress: ["radix.import.progress"],
  printFile: (importId: string) => ["radix.print", importId],
};

// ── Ustawienia ───────────────────────────────────────────────────────────────

const getSettings = (): Promise<{ data: IRadixSettings }> =>
  axiosInstance.get("radix/settings");

const useGetSettings = (enabled = true) =>
  useCustomQuery(radixQueryKeys.settings, getSettings, { enabled, retry: false });

const saveSettings = (
  request: ISaveRadixSettingsRequest,
): Promise<{ data: IRadixSettings }> =>
  axiosInstance.post("radix/settings", request);

const useSaveSettings = () => useCustomMutation(saveSettings);

const testConnection = (
  request: ITestRadixConnectionRequest,
): Promise<{ data: ITestRadixConnectionResponse }> =>
  axiosInstance.post("radix/settings/test", request);

const useTestConnection = () => useCustomMutation(testConnection);

// ── Dokumenty w bazie RADIX ──────────────────────────────────────────────────

const getSourceDocuments = (
  program: ERadixProgram,
): Promise<{ data: IRadixSourceDocumentsResponse }> =>
  axiosInstance.get(`radix/source/${program}`);

const useGetSourceDocuments = (program: ERadixProgram, enabled = true) =>
  useCustomQuery(
    radixQueryKeys.source(program),
    () => getSourceDocuments(program),
    { enabled, retry: false },
  );

// ── Import ───────────────────────────────────────────────────────────────────

/** Multipart: plik RTF + parametry jako JSON w polu `request`. */
const startImport = (payload: {
  file: File;
  request: IRadixImportRequest;
}): Promise<{ data: IRadixImportStartResponse }> => {
  const form = new FormData();
  form.append("file", payload.file, payload.file.name);
  form.append("request", JSON.stringify(payload.request));
  return axiosInstance.post("radix/import", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

const useStartImport = () => useCustomMutation(startImport);

const getImportProgress = (): Promise<{ data: IRadixImportProgress }> =>
  axiosInstance.get("radix/import/progress");

const getBatchPrintFile = (
  importId: string,
): Promise<{ data: IRadixFilePreview }> =>
  axiosInstance.get(`radix/import/${importId}/print`);

const useGetBatchPrintFile = (importId: string | null) =>
  useCustomQuery(
    radixQueryKeys.printFile(importId ?? ""),
    () => getBatchPrintFile(importId!),
    { enabled: !!importId },
  );

// ── Listy ────────────────────────────────────────────────────────────────────

const getList = (
  view: ERadixListView,
  search: string,
): Promise<{ data: IRadixListResponse }> =>
  axiosInstance.post("radix/list", { View: view, Search: search || null });

const useGetList = (view: ERadixListView, search: string) =>
  useCustomQuery(radixQueryKeys.list(view, search), () => getList(view, search));

// ── Akcje obiegu ─────────────────────────────────────────────────────────────

const action =
  (path: string) =>
  (
    request: IRadixDocumentsActionRequest,
  ): Promise<{ data: IRadixDocumentsActionResponse }> =>
    axiosInstance.post(`radix/${path}`, request);

const useSetReadyToDispatch = () =>
  useCustomMutation(action("setReadyToDispatch"));
const useReturnToProject = () => useCustomMutation(action("returnToProject"));
const useMarkAsSent = () => useCustomMutation(action("markAsSent"));
const useReturnToSend = () => useCustomMutation(action("returnToSend"));
const useRemove = () => useCustomMutation(action("remove"));

const generateForAllReceivers = (
  request: IRadixGenerateForAllRequest,
): Promise<{ data: IRadixGenerateForAllResponse }> =>
  axiosInstance.post("radix/generateForAllReceivers", request);

const useGenerateForAllReceivers = () =>
  useCustomMutation(generateForAllReceivers);

const radixService = {
  useGetSettings,
  useSaveSettings,
  useTestConnection,
  useGetSourceDocuments,
  useStartImport,
  getImportProgress,
  useGetBatchPrintFile,
  useGetList,
  useSetReadyToDispatch,
  useReturnToProject,
  useMarkAsSent,
  useReturnToSend,
  useRemove,
  useGenerateForAllReceivers,
};

export default radixService;
